import { Loader } from '@refera/ui-web'

import React, { useCallback, useEffect, useState } from 'react'
import HeaderTitle from '_/components/header-title'
import { Grid, Button } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useToast } from '_/hooks/use-toast'
import { navigate, useParams } from '@reach/router'
import useStyles from './styles'
import { BasicInput, Checkbox } from '_/components/inputs'
import { useDispatch } from 'react-redux'
import ModalDialog, { WARNING_MODAL } from '_/components/modal/modal-dialog'
import { createSlug } from '_/utils/permissions'
import {
  editMacroStep,
  getMacroStepById,
  getMacroStepsCustom,
  postMacroStep,
} from '_/modules/agency/actions'

export default function MacroStepForm() {
  const dispatch = useDispatch()
  const styles = useStyles()
  const [defaultMacroStep, setEditMacroStep] = useState(null)
  const [defaultFieldsValues, setDefaultFieldsValues] = useState(null)

  // const { checkIfUserDoesNotPermission } = useRolePermission()

  const [modalDialog, setModalDialog] = useState({
    isOpen: false,
    textOkButton: 'Ok, entendi',
    subTitle: `Salve antes as edições deste registro.`,
    type: WARNING_MODAL,
  })

  const { macroStepId, agencyId } = useParams()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { showToast } = useToast()

  // Form constants
  const methods = useForm({
    mode: 'all',
    shouldUnregister: false,
    defaultValues: {
      name: '',
      tip: '',
      alert: '',
      expectedTime: '',
      requiredTip: false,
      requiredAlert: false,
      requiredExpectedTime: false,
    },
  })

  const { handleSubmit, reset, setValue, watch } = methods

  const requiredTip = watch('requiredTip')
  const requiredAlert = watch('requiredAlert')
  const requiredExpectedTime = watch('requiredExpectedTime')

  const resetFields = useCallback(() => {
    if (!requiredTip) {
      setValue('tip', null)
    }

    if (!requiredAlert) {
      setValue('alert', null)
    }

    if (!requiredExpectedTime) {
      setValue('expectedTime', '')
    }
  }, [requiredTip, requiredAlert, requiredExpectedTime])

  useEffect(() => {
    resetFields()
  }, [requiredTip, requiredAlert, requiredExpectedTime])

  const goBack = () => {
    navigate(-1)
  }

  const macroStageEstimatedTimeText = useCallback(estimatedTime => {
    const macroStageEstimatedTime = Math.ceil(estimatedTime ?? 0)
    return macroStageEstimatedTime.toString()
  }, [])

  const handleSubmitForm = useCallback(
    data => {
      const newData = {
        ...data,
        name: createSlug(data.desc),
      }

      if (defaultMacroStep?.id) {
        // if (checkIfUserDoesNotPermission(PERMISSIONS.PERMISSION_GROUPS, PERMISSIONS_ACTIONS.ADD)) {
        //   return null
        // }

        return dispatch(
          editMacroStep(defaultMacroStep?.id, {
            expectedTime: newData?.expectedTime || null,
            alert: newData?.alert || null,
            tip: newData?.tip || null,
          })
        )
          .then(() => {
            getMacroStep()
            showToast({ type: 'success' })
          })
          .catch(() => {
            return showToast({
              type: 'error',
              message: 'Ocorreu um erro ao editar macro etapa.',
            })
          })
      }

      return dispatch(
        postMacroStep({
          expectedTime: newData?.expectedTime || null,
          alert: newData?.alert || null,
          tip: newData?.tip || null,
          agency: agencyId,
          stepNameForCustomer: macroStepId,
        })
      )
        .then(() => {
          getMacroStep()
          showToast({ type: 'success' })
        })
        .catch(() => {
          return showToast({
            type: 'error',
            message: 'Ocorreu um erro ao editar macro etapa.',
          })
        })
    },
    [agencyId, macroStepId, defaultMacroStep]
  )

  const getMacroStep = useCallback(() => {
    if (macroStepId) {
      setIsSubmitting(true)

      return dispatch(getMacroStepById(macroStepId))
        .then(data => {
          setDefaultFieldsValues({
            nameDefault: data?.name,
            expectedTimeDefault: macroStageEstimatedTimeText(data?.expectedTime),
            alertDefault: data?.alert,
            tipDefault: data?.tip,
          })

          dispatch(
            getMacroStepsCustom({ agency: agencyId, stepNameForCustomer: macroStepId })
          ).then(dataCustom => {
            setEditMacroStep(dataCustom?.results?.[0])
            setValue('requiredTip', !!dataCustom?.results?.[0]?.tip)
            setValue('requiredAlert', !!dataCustom?.results?.[0]?.alert)
            setValue('requiredExpectedTime', !!dataCustom?.results?.[0]?.expectedTime)
          })
          return setIsSubmitting(false)
        })
        .catch(() => {
          showToast({ type: 'error', message: 'Ocorreu um erro ao buscar macro etapa.' })
          return setIsSubmitting(false)
        })
    }
    return null
  }, [dispatch, macroStepId, agencyId])

  function handleReset() {
    if (macroStepId) {
      getMacroStep()
    }

    return reset()
  }

  useEffect(() => {
    getMacroStep()
  }, [macroStepId, agencyId])

  useEffect(() => {
    if (defaultMacroStep) {
      setValue('nameDefault', defaultMacroStep?.name)
      setValue('expectedTime', defaultMacroStep?.expectedTime)
      setValue('alert', defaultMacroStep?.alert)
      setValue('tip', defaultMacroStep?.tip)
    }
  }, [defaultMacroStep])

  return (
    <Grid className={styles.app}>
      <HeaderTitle
        title={`Etapas para clientes/${defaultFieldsValues?.nameDefault}`}
        backButtonAction={goBack}
      >
        <Grid style={{ display: 'flex', gap: '8px' }}>
          <Button className={[styles.buttonRed, styles.button]} onClick={() => handleReset()}>
            Cancelar
          </Button>
          <Button
            className={[styles.buttonFullBlue, styles.button]}
            form="edit-macro-step"
            onClick={handleSubmit(handleSubmitForm)}
          >
            Salvar
          </Button>
        </Grid>
      </HeaderTitle>
      <FormProvider {...methods}>
        <form
          id="edit-macro-step"
          onSubmit={handleSubmit(handleSubmitForm)}
          className={styles.container}
        >
          <BasicInput
            label="Nome da etapa"
            name="nameDefault"
            value={defaultFieldsValues?.nameDefault}
            defaultValue={defaultFieldsValues?.nameDefault}
            disabled
            containerClasses={styles.input}
            labelClasses={styles.label}
          />

          <div className={styles.form}>
            <div className={styles.field}>
              {requiredExpectedTime ? (
                <BasicInput
                  label="Horas úteis na etapa"
                  name="expectedTime"
                  disabled={!requiredExpectedTime}
                  containerClasses={styles.input}
                  labelClasses={styles.label}
                  type="number"
                />
              ) : (
                <BasicInput
                  label="Horas úteis na etapa"
                  name="expectedTimeDefault"
                  disabled
                  multiline
                  defaultValue={defaultFieldsValues?.expectedTimeDefault}
                  value={defaultFieldsValues?.expectedTimeDefault}
                  containerClasses={styles.input}
                  labelClasses={styles.label}
                />
              )}
              <Checkbox label="Quero personalizar" name="requiredExpectedTime" />
            </div>

            <div className={styles.field}>
              {requiredTip ? (
                <BasicInput
                  label="Explicação sobre a etapa"
                  name="tip"
                  disabled={!requiredTip}
                  containerClasses={styles.input}
                  labelClasses={styles.label}
                  multiline
                  rules={{
                    maxLength: 1000,
                  }}
                />
              ) : (
                <BasicInput
                  label="Explicação sobre a etapa"
                  name="tipDefault"
                  disabled
                  multiline
                  defaultValue={defaultFieldsValues?.tipDefault}
                  value={defaultFieldsValues?.tipDefault}
                  containerClasses={styles.input}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: 1000,
                  }}
                />
              )}
              <Checkbox label="Quero personalizar" name="requiredTip" />
            </div>

            <div className={styles.field}>
              {requiredAlert ? (
                <BasicInput
                  label="Alerta sobre a etapa"
                  name="alert"
                  disabled={!requiredAlert}
                  containerClasses={styles.input}
                  labelClasses={styles.label}
                  multiline
                  rules={{
                    maxLength: 1000,
                  }}
                />
              ) : (
                <BasicInput
                  label="Alerta sobre a etapa"
                  name="alertDefault"
                  disabled
                  defaultValue={defaultFieldsValues?.alertDefault}
                  value={defaultFieldsValues?.alertDefault}
                  containerClasses={styles.input}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: 1000,
                  }}
                />
              )}
              <Checkbox label="Quero personalizar" name="requiredAlert" />
            </div>
          </div>
        </form>
      </FormProvider>

      <ModalDialog modalDialog={modalDialog} setModalDialog={setModalDialog} />
      <Loader hasBackdrop open={isSubmitting} label="Aguarde..." />
    </Grid>
  )
}
